import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { AsyncTypeahead, Highlighter } from 'react-bootstrap-typeahead';
import { Label } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { searchConcept, searchConceptByExpression, searchFrequentConcepts } from '../../api/snomed';
import { HandleApiError } from "../Notifications/APIErrorHandler";
import { useAutocompleteDropdownSize } from './hooks/useAutocompleteDropdownSize';

import 'react-bootstrap-typeahead/css/Typeahead.css';

export const SnomedPicker = (props) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [options, setOptions] = useState([]);
    const dropdownSize = useAutocompleteDropdownSize();

    const handleChange = value => {
        if (value.length > 0) {
            if (props.multiple)
                props.onChange(props.name, value);
            else
                props.onChange(props.name, value[0]);
        }
        else
            props.onChange(props.name, null);
    };

    const handleBlur = () => {
        if (props.onBlur)
            props.onBlur(props.name, true);
    };

    const onSearch = async (term) => {
        setIsLoading(true);

        let func = null;
        let query = "";

        if (props.conceptId) {
            func = searchConcept;
            query = props.conceptId;
        }
        else {
            func = searchConceptByExpression;
            query = props.expression;
        }

        // Se disparan en paralelo: el catálogo externo (Snowstorm/ICD10/etc.) y los términos frecuentes
        // del tenant. Se usa allSettled (no all) para que la caída de uno no tumbe al otro (degradación
        // con gracia - si /concept/frequent falla, el picker sigue mostrando sólo el catálogo).
        const [catalogResult, frequentResult] = await Promise.allSettled([
            func(query, encodeURIComponent(term), props.conceptType),
            searchFrequentConcepts(props.conceptType, term, dropdownSize)
        ]);

        setIsLoading(false);

        if (catalogResult.status === 'rejected')
            HandleApiError(catalogResult.reason);

        if (frequentResult.status === 'rejected')
            console.error('searchFrequentConcepts failed', frequentResult.reason);

        const catalogOptions = catalogResult.status === 'fulfilled' && Array.isArray(catalogResult.value.data)
            ? catalogResult.value.data
            : [];
        const frequentOptions = frequentResult.status === 'fulfilled' && Array.isArray(frequentResult.value.data)
            ? frequentResult.value.data
            : [];

        // Los frecuentes van primero (ya vienen ordenados por uso desde el backend), sin duplicar
        // términos que ya aparecieron como frecuentes, completando hasta el total del dropdown.
        const frequentConceptIds = new Set(frequentOptions.map(x => x.conceptId));
        const merged = [
            ...frequentOptions.map(x => ({ ...x, isFrequent: true })),
            ...catalogOptions.filter(x => !frequentConceptIds.has(x.conceptId))
        ].slice(0, dropdownSize);

        setOptions(merged);
    }

    return <AsyncTypeahead
        {...props}
        labelKey="description"
        filterBy={() => true}
        options={Array.isArray(options) ? options : []}
        isLoading={isLoading}
        onSearch={onSearch}
        highlightOnlyResult={true}
        delay={500}
        onChange={handleChange}
        onBlur={handleBlur}
        renderMenuItemChildren={(option, menuProps) => (
            <>
                {option.isFrequent && <>
                    <Label bsStyle="info">{t("commons.frequent")}</Label>{' '}
                </>}
                <Highlighter search={menuProps.text}>{option.description}</Highlighter>
            </>
        )}
    />;
}

SnomedPicker.propTypes = {
    multiple: PropTypes.bool,
    onChange: PropTypes.func,
    minLength: PropTypes.number,
    conceptId: PropTypes.string,
    expression: PropTypes.string,
    clearButton: PropTypes.bool,
    conceptType: PropTypes.string
};

SnomedPicker.defaultProps = {
    multiple: false,
    onChange: () => { },
    minLength: 3,
    allowNew: false,
    clearButton: true
};
